import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { authApi } from '../../api/auth';
import { errorMessage } from '../../api/client';
import { isChallenge, type AuthChallenge } from '../../api/types';
import { homeFor, useAuth } from '../../auth/AuthContext';
import { passwordProblem, useAuthRouteState } from '../../auth/route-state';
import { Alert, Button, TextField } from '../../components/ui';

const COPY: Record<
  AuthChallenge['challengeName'],
  { title: string; lead: (c: AuthChallenge) => string }
> = {
  NEW_PASSWORD_REQUIRED: {
    title: 'Choose a new password',
    lead: () => 'Your account was created with a temporary password. Set your own to continue.',
  },
  SMS_MFA: {
    title: 'Check your phone',
    lead: (c) => `Enter the verification code we texted to ${c.destination ?? 'your phone'}.`,
  },
  SMS_OTP: {
    title: 'Check your phone',
    lead: (c) => `Enter the sign-in code we texted to ${c.destination ?? 'your phone'}.`,
  },
  SOFTWARE_TOKEN_MFA: {
    title: 'Two-factor authentication',
    lead: () => 'Enter the six-digit code from your authenticator app.',
  },
};

/**
 * Second step of login (POST /auth/challenge). Which fields show depends on
 * the challenge the provider returned; a follow-up challenge simply re-renders.
 */
export function Challenge() {
  const navigate = useNavigate();
  const { completeSignIn } = useAuth();
  const { challenge: initial, from } = useAuthRouteState();
  const [challenge, setChallenge] = useState<AuthChallenge | undefined>(initial);
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);

  const answer = useMutation({
    mutationFn: async () => {
      if (!challenge) return;
      const needsPassword = challenge.challengeName === 'NEW_PASSWORD_REQUIRED';
      if (needsPassword) {
        const problem = passwordProblem(newPassword, confirm);
        if (problem) {
          setFieldError(problem);
          return;
        }
      }
      setFieldError(null);
      const result = await authApi.challenge({
        username: challenge.username,
        challengeName: challenge.challengeName,
        session: challenge.session,
        ...(needsPassword ? { newPassword } : { code: code.trim() }),
      });
      if (isChallenge(result)) {
        setChallenge(result);
        setCode('');
        return;
      }
      const me = await completeSignIn(result);
      navigate(from ?? homeFor(me), { replace: true });
    },
  });

  if (!challenge) return <Navigate to="/sign-in" replace />;
  const copy = COPY[challenge.challengeName];
  const needsPassword = challenge.challengeName === 'NEW_PASSWORD_REQUIRED';

  return (
    <>
      <h1 className="auth__title">{copy.title}</h1>
      <p className="auth__lead">{copy.lead(challenge)}</p>
      {answer.error ? <Alert kind="error">{errorMessage(answer.error)}</Alert> : null}
      <form
        className="form"
        onSubmit={(e) => {
          e.preventDefault();
          answer.mutate();
        }}
        noValidate
      >
        {needsPassword ? (
          <>
            <TextField
              label="New password"
              name="newPassword"
              type="password"
              autoComplete="new-password"
              autoFocus
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              hint="At least 8 characters. The pool's password policy also applies."
            />
            <TextField
              label="Confirm new password"
              name="confirmPassword"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              error={fieldError}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </>
        ) : (
          <TextField
            label="Verification code"
            name="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            required
            maxLength={10}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        )}
        <Button
          type="submit"
          loading={answer.isPending}
          disabled={needsPassword ? !newPassword || !confirm : code.trim().length < 4}
        >
          {needsPassword ? 'Set password and sign in' : 'Verify and sign in'}
        </Button>
      </form>
      <p className="auth__switch">
        <Link to="/sign-in">Start over</Link>
      </p>
    </>
  );
}
